/**
 * RGPD opérationnel — Phase 9.
 *
 * Deux actions réelles (remplacent les placeholders « Bientôt
 * disponible ») :
 *
 *   - **Exporter données** : télécharge un JSON de tout l'état
 *     structuré local (foyers + préférences + langue). Les médias
 *     binaires (photos/vocaux IndexedDB) ne sont pas embarqués dans
 *     le JSON — leur présence est signalée dans les données foyers
 *     (sentinels `[idb]`).
 *   - **Supprimer compte (local)** : après confirmation explicite,
 *     efface tout l'état local (localStorage `nestor_*` + base
 *     IndexedDB `nestor`) puis relance l'app sur l'onboarding.
 *     La suppression du compte **serveur** (utilisateur authentifié)
 *     arrive avec l'incrément 9B — l'UI l'indique dans le texte de
 *     confirmation.
 */
import { t } from '../../i18n/index.js';
import { getSession } from '../../stores/session.js';
import { deleteAccount } from '../../api/auth.js';
import { showToast } from '../../ui/Toast.js';
import { confirmDialog } from '../../ui/Dialog.js';
import { STORAGE_KEY_HOMES, getHomesSnapshot } from '../../stores/homes.js';
import { STORAGE_KEY_PREFS, getPreferences } from '../../stores/preferences.js';
import { getLang } from '../../i18n/index.js';
import { DB_NAME } from '../../storage/db.js';

/** Clés localStorage possédées par Nestor (à purger à la suppression). */
const NESTOR_STORAGE_KEYS = [
  STORAGE_KEY_HOMES,
  STORAGE_KEY_PREFS,
  'nestor_lang',
  'nestor_ai',
  'nestor_weather',
];

/**
 * Construit le payload d'export RGPD (pur, testable) : instantané
 * complet de l'état structuré local.
 */
export function buildExportPayload(): Record<string, unknown> {
  const { homes, currentHomeId } = getHomesSnapshot();
  return {
    app: 'Nestor',
    format: 'nestor-export/1',
    exportedAt: new Date().toISOString(),
    lang: getLang(),
    preferences: getPreferences(),
    currentHomeId,
    homes,
  };
}

/** Télécharge le payload en fichier `nestor-export-YYYY-MM-DD.json`. */
export function exportAllData(): void {
  const payload = buildExportPayload();
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `nestor-export-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Laisse au navigateur le temps de démarrer le téléchargement.
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/**
 * Efface TOUTES les données locales après confirmation. Retourne
 * `true` si la suppression a été confirmée et lancée.
 *
 * Phase 9B : si un compte serveur est connecté, il est supprimé
 * D'ABORD (`DELETE /auth/me` — foyers solo compris). En cas d'échec
 * réseau/API, on n'efface PAS le local : supprimer l'appareil en
 * laissant le compte serveur vivant serait trompeur.
 */
export async function deleteLocalAccount(): Promise<boolean> {
  const authenticated = getSession().status === 'authenticated';
  const confirmed = await confirmDialog({
    title: t('account_delete'),
    message: authenticated ? t('gdpr_delete_confirm_auth') : t('gdpr_delete_confirm'),
    confirmLabel: t('account_delete'),
    danger: true,
  });
  if (!confirmed) return false;

  if (authenticated) {
    try {
      await deleteAccount();
    } catch {
      showToast({ message: t('account_err_network'), kind: 'error' });
      return false;
    }
  }

  for (const key of NESTOR_STORAGE_KEYS) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // storage indisponible — on continue la purge au mieux.
    }
  }
  try {
    indexedDB.deleteDatabase(DB_NAME);
  } catch {
    // IDB indisponible (vieux navigateur) — les clés localStorage
    // sont déjà purgées, l'état structurel est parti.
  }
  // Redémarre proprement sur l'onboarding (state mémoire compris).
  window.location.hash = '#/onboarding';
  window.location.reload();
  return true;
}
